/**
 * r16q6 (Live mode, docs/LIVE-MODE-DESIGN.md section 8): which phone a chat's slot is on, from what the page already has: the slot list
 * (`device_id`), the device rows (`udid`) and the PCs' presence (who reports that serial right now). A slot with no phone assigned can show
 * one the owner picks; that choice stays in this browser and changes nothing on the server, so no worker is ever pointed at that phone.
 */
import { deviceIdOf } from './monitorAssoc';
import { slotKey } from './slotSelection';

const LS_SHOW = 'necro.ui.inbox.showPhone';
const LS_PICK = 'necro.ui.inbox.phonePick.';

const clean = (v) => (v === null || v === undefined ? '' : String(v).trim());

/** What the row of one device row offers: {id, label, serial}. */
function deviceOf(row) {
  const id = deviceIdOf(row);
  const serial = clean(row && row.udid);
  if (!id || !serial) return null;
  return { id: String(id), serial, label: clean(row.label) || clean(row.device_name) || String(id) };
}

/** Every phone of the account, for the "show this phone" list. Pure. */
export function phoneChoices(devices) {
  const seen = new Set();
  const out = [];
  (Array.isArray(devices) ? devices : []).forEach((row) => {
    const d = deviceOf(row);
    if (d && !seen.has(d.id)) {
      seen.add(d.id);
      out.push(d);
    }
  });
  return out;
}

/** The PC reporting this serial on adb right now, or null. Pure. */
export function pcForSerial(presence, serial) {
  const clients = (presence && presence.clients) || [];
  for (const c of clients) {
    if (!c.client_connected) continue;
    if ((c.devices || []).some((d) => d.serial === serial && d.state === 'device')) {
      return { installId: c.installId, pc: clean(c.hostname) || clean(c.label) };
    }
  }
  return null;
}

/**
 * The phone to show for one slot. Pure.
 * @returns {{status: 'loading'|'none'|'ok', assigned: boolean, deviceId?: string, label?: string, serial?: string, installId?: *, pc?: string, key?: string}}
 */
export function resolveChatPhone({ slotName, slots, devices, presence, pickedId }) {
  if (!slotName) return { status: 'none', assigned: false };
  if (!Array.isArray(slots) || !Array.isArray(devices)) return { status: 'loading', assigned: false };
  const slot = slots.find((s) => slotKey(s) === slotName);
  const assignedId = clean(slot && slot.device_id);
  const choices = phoneChoices(devices);
  let d = assignedId ? choices.find((c) => c.id === assignedId) : null;
  const assigned = !!d;
  if (!d && pickedId) d = choices.find((c) => c.id === pickedId) || null;
  if (!d) return { status: 'none', assigned: false };
  const at = pcForSerial(presence, d.serial);
  return {
    status: 'ok',
    assigned,
    deviceId: d.id,
    label: d.label,
    serial: d.serial,
    installId: at ? at.installId : null,
    pc: at ? at.pc : '',
    key: `${at ? at.installId : 'here'}::${d.serial}`,
  };
}

/** The route for this phone: this PC's own relay when it answers (the fastest), else through the server when a PC reports it. Pure. */
export function phoneRoute(localAnswers, installId) {
  if (localAnswers) return { transport: 'local', autoFallback: installId !== null && installId !== undefined };
  if (installId !== null && installId !== undefined) return { transport: 'remote', autoFallback: false };
  return null;
}

function store() {
  try {
    return window.localStorage;
  } catch (e) {
    return null;
  }
}

function read(k) {
  try {
    const s = store();
    return s ? s.getItem(k) : null;
  } catch (e) {
    return null;
  }
}

function write(k, v) {
  try {
    const s = store();
    if (s) s.setItem(k, v);
  } catch (e) {
    /* a private window: it just isn't remembered */
  }
}

/** On by default (owner's ruling R2); the untick is remembered per browser. */
export const getShowPhone = () => read(LS_SHOW) !== 'off';
export const setShowPhone = (on) => write(LS_SHOW, on ? 'on' : 'off');
export const getPickedPhone = (slotName) => read(LS_PICK + slotName);
export const setPickedPhone = (slotName, id) => write(LS_PICK + slotName, id);
